import type { ReactNode } from 'react';
import { Skeleton, Text } from '@gravity-ui/uikit';

/**
 * Полоса показателей — одна панель, показатели через вертикальные
 * разделители, как сводка биллинга в консоли: «Пришло · Ушло · Итог» читаются
 * одной строкой и сравниваются взглядом, а не раскладываются по одинаковым
 * карточкам.
 */

export interface Metric {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'positive' | 'danger' | 'primary';
  /** Сделать показатель ссылкой — «каждая цифра ведёт к первичному документу». */
  href?: string;
  onOpen?: () => void;
}

export function MetricStrip({ metrics, loading = false }: { metrics: readonly Metric[]; loading?: boolean }) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-[var(--g-color-line-generic)] bg-[var(--g-color-base-background)] sm:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className="-mt-px -ml-px flex min-w-0 flex-col gap-1 border-t border-l border-[var(--g-color-line-generic)] px-5 py-4"
        >
          <Text variant="body-1" color="secondary" ellipsis>
            {metric.label}
          </Text>
          {loading ? (
            <Skeleton style={{ height: 28, width: '70%', borderRadius: 6 }} />
          ) : (
            <MetricValue metric={metric} />
          )}
          {metric.hint === undefined || loading ? null : (
            <Text variant="caption-2" color="secondary">
              {metric.hint}
            </Text>
          )}
        </div>
      ))}
    </div>
  );
}

function MetricValue({ metric }: { metric: Metric }) {
  const color = metric.tone === 'positive' ? 'positive' : metric.tone === 'danger' ? 'danger' : 'primary';
  const value = (
    <Text variant="header-1" color={color} className="tabular" whiteSpace="nowrap">
      {metric.value}
    </Text>
  );
  if (metric.href === undefined) return value;
  return (
    <a
      href={metric.href}
      className="w-fit rounded-md no-underline outline-offset-2 hover:opacity-80"
      onClick={(event) => {
        if (metric.onOpen === undefined || event.button !== 0 || event.metaKey || event.ctrlKey) return;
        event.preventDefault();
        metric.onOpen();
      }}
    >
      {value}
    </a>
  );
}
